import React, { useState, useEffect } from 'react';
import PageSEO from '../components/PageSEO';
import { SITE_INFO, BRAND_ASSETS } from '../data';
import { getTeamMembers, getSiteSettings } from '../lib/cmsClient';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';

export default function AboutPage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const workImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kts-maskine-firmabil.webp",
      alt: "KTS-Entreprise gravemaskine og firmabil på arbejdsplads",
      caption: "KTS-Entreprise gravemaskine og firmabil på arbejdsplads"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/entreprenoer-liebherr-gravemaskine.webp",
      alt: "Liebherr gravemaskine på KTS-Entreprise arbejdsplads",
      caption: "Liebherr gravemaskine på KTS-Entreprise arbejdsplads"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/om-os-graesarmering.webp",
      alt: "Græsarmering udført af KTS-Entreprise",
      caption: "Græsarmering udført af KTS-Entreprise"
    }
  ];

  const [team, setTeam] = useState(SITE_INFO.contacts);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    async function loadAboutData() {
      try {
        const [teamData, settingsData] = await Promise.all([
          getTeamMembers(),
          getSiteSettings()
        ]);
        if (teamData && teamData.length > 0) {
          setTeam(teamData);
        }
        if (settingsData) {
          setSettings(settingsData);
        }
      } catch (err) {
        console.warn("Could not load CMS data on AboutPage:", err);
      }
    }
    loadAboutData();
  }, []);

  const aboutPhoto = settings.about_image?.image || "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/dsc4768b-edited.jpg";

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Om KTS-Entreprise | Lokalt forankret entreprenør siden 1986"
        description="KTS-Entreprise er en lokalt forankret entreprenørvirksomhed med base i Frederiksværk. Vi udfører jord-, kloak-, anlægs- og betonarbejde i Nordsjælland og omegn."
        ogTitle="Om KTS-Entreprise | KTS-Entreprise A/S"
        ogDescription="KTS-Entreprise er en lokalt forankret entreprenørvirksomhed med base i Frederiksværk. Cirka 20 medarbejdere inden for bygge og anlæg, kloak, værksted og administration."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="om-os"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Siden 1986</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Om KTS-Entreprise
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            Lokalt forankret entreprenørvirksomhed med base i Frederiksværk.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        
        {/* Intro & History Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          <div className="lg:col-span-7 space-y-8">
            
            {/* Indledning */}
            <div className="space-y-4 text-stone-800 text-base sm:text-lg leading-relaxed font-medium">
              <p className="text-xl sm:text-2xl font-bold text-stone-900 leading-snug">
                KTS-Entreprise er en lokalt forankret entreprenørvirksomhed med base i Frederiksværk. Vi udfører jord-, kloak-, anlægs- og betonarbejde for private, erhverv og offentlige bygherrer i Nordsjælland og omegn.
              </p>
            </div>

            {/* Vores historie */}
            <div className="space-y-3 bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs">
              <div className="border-l-4 border-[#0072c0] pl-3.5 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Vores historie
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Virksomheden blev grundlagt i 1986 som vognmandsfirma. I slutningen af 1990’erne udviklede KTS-Entreprise sig til den entreprenørvirksomhed, vi er i dag.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                I dag beskæftiger virksomheden cirka 20 medarbejdere inden for bygge og anlæg, kloak, værksted og administration.
              </p>
            </div>

            {/* Det arbejder vi med */}
            <div className="space-y-3 bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs">
              <div className="border-l-4 border-[#0072c0] pl-3.5 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Det arbejder vi med
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Vores primære arbejdsområder er entreprenør- og anlægsarbejde, autoriseret kloakarbejde, klimasikring, jordarbejde og betonarbejde.
              </p>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Derudover sælger vi materialer fra vores plads i Frederiksværk og udlejer blandt andet jernkøreplader, gangbroer og byggepladsudstyr.
              </p>
            </div>

            {/* Sådan arbejder vi */}
            <div className="space-y-3 bg-white p-6 sm:p-8 border border-stone-200 rounded-xs shadow-xs">
              <div className="border-l-4 border-[#0072c0] pl-3.5 mb-2">
                <h2 className="text-xl sm:text-2xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Sådan arbejder vi
                </h2>
              </div>
              <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                Vi lægger vægt på ordentlige aftaler, god kommunikation og løsninger, der passer til den enkelte opgave.
              </p>
            </div>

            {/* Mindre række med autentiske arbejdsbilleder */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
              <div className="bg-white border border-stone-200 p-2 rounded-xs shadow-xs">
                <ClickableWorkImage
                  src={workImages[0].src}
                  alt={workImages[0].alt}
                  className="w-full h-36 sm:h-32 md:h-36 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 0 })}
                />
              </div>
              <div className="bg-white border border-stone-200 p-2 rounded-xs shadow-xs">
                <ClickableWorkImage
                  src={workImages[1].src}
                  alt={workImages[1].alt}
                  className="w-full h-36 sm:h-32 md:h-36 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 1 })}
                />
              </div>
              <div className="bg-white border border-stone-200 p-2 rounded-xs shadow-xs">
                <ClickableWorkImage
                  src={workImages[2].src}
                  alt={workImages[2].alt}
                  className="w-full h-36 sm:h-32 md:h-36 object-cover rounded-xs"
                  onClick={() => setLightboxState({ isOpen: true, index: 2 })}
                />
              </div>
            </div>

          </div>

          {/* Right Column: Visuals & Fast Facts */}
          <div className="lg:col-span-5 space-y-6">
            <div className="overflow-hidden border border-stone-200 rounded-xs shadow-sm bg-white p-2">
              <img
                src="https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/kts-medarbejdere-gruppe.webp"
                alt="Medarbejdere hos KTS-Entreprise foran firmabilerne"
                className="w-full h-auto object-contain rounded-xs"
              />
            </div>

            <div className="bg-stone-900 text-white p-6 sm:p-8 rounded-xs border border-stone-800 space-y-4">
              <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#0072c0] block">
                Nøgletal &amp; Fakta
              </span>
              <ul className="space-y-3 text-xs sm:text-sm text-stone-300">
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-calendar-check text-[#0072c0]" aria-hidden="true"></i>
                  <span>Grundlagt i 1986 i Nordsjælland</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-users text-[#0072c0]" aria-hidden="true"></i>
                  <span>Cirka 20 dedikerede medarbejdere</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-shield-halved text-[#0072c0]" aria-hidden="true"></i>
                  <span>Autoriseret kloakmestervirksomhed</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-certificate text-[#0072c0]" aria-hidden="true"></i>
                  <span>Certificeret rottespærremontør</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-file-shield text-[#0072c0]" aria-hidden="true"></i>
                  <span>Miljøgodkendt plads i Frederiksværk</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="fa-solid fa-handshake-angle text-[#0072c0]" aria-hidden="true"></i>
                  <span>Medlem af DI Byggeri &amp; Byg Garanti</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Autorisationer og godkendelser */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          
          <div className="bg-white border border-stone-200 p-6 sm:p-8 rounded-xs shadow-xs space-y-3">
            <div className="border-l-4 border-[#0072c0] pl-3.5 mb-2">
              <h3 className="text-lg sm:text-xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                Autorisationer &amp; Godkendelser
              </h3>
            </div>
            <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
              KTS-Entreprise er autoriseret kloakmestervirksomhed, certificeret rottespærremontør og medlem af DI Byggeri med tilvalg af Byg Garanti.
            </p>
            <p className="text-stone-700 text-sm sm:text-base leading-relaxed mt-2">
              Virksomhedens plads på Industrivej 3 i Frederiksværk er miljøgodkendt til de godkendte aktiviteter vedrørende modtagelse og håndtering af bygge- og anlægsmaterialer.
            </p>
            <p className="text-stone-700 text-sm sm:text-base leading-relaxed mt-2 font-medium">
              Hos KTS-Entreprise udføres montering og service af rottespærrer af en certificeret rottespærremontør.
            </p>
          </div>

          {/* Sektion 15: Medlem af DI Byggeri og Byg Garanti */}
          <div className="bg-slate-50 border border-slate-200 p-6 sm:p-8 rounded-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-4">
              <div className="flex items-center gap-4">
                <img
                  src={BRAND_ASSETS.diBadgeLarge}
                  alt="Medlem af DI Byggeri"
                  className="h-10 w-auto object-contain"
                />
                <h3 className="text-lg sm:text-xl font-bold uppercase tracking-tight text-stone-900 font-heading">
                  Medlem af DI Byggeri og Byg Garanti
                </h3>
              </div>
            </div>

            <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
              KTS-Entreprise er medlem af DI Byggeri og tilsluttet Byg Garanti. Ordningen giver private forbrugere en ekstra sikkerhed ved bestemte typer håndværksarbejde. Dækningen afhænger af arbejdets type og de til enhver tid gældende betingelser.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href={BRAND_ASSETS.diLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#0072c0] hover:bg-[#005b99] text-white text-xs sm:text-sm font-heading font-bold uppercase tracking-wider px-4 py-2.5 rounded-xs transition-colors"
              >
                <span>Bekræft KTS-Entreprises medlemskab</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
              </a>

              <a
                href={BRAND_ASSETS.bygGarantiConditionsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-xs sm:text-sm font-heading font-bold uppercase tracking-wider px-4 py-2.5 rounded-xs transition-colors"
              >
                <span>Læs de gældende betingelser hos Byg Garanti</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Kontakt nøglepersonerne */}
        <div className="space-y-8">
          <div className="border-l-4 border-[#0072c0] pl-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Kontakt nøglepersonerne
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-1">
              Her finder du kontaktoplysninger på nøglepersonerne inden for ledelse, administration, kloak, TV-inspektion og værksted.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((contact) => (
              <div
                key={contact.name}
                className="bg-white border border-stone-200 p-6 rounded-xs shadow-xs flex flex-col justify-between hover:border-[#0072c0]/40 transition-colors"
              >
                <div>
                  <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-[#0072c0] block mb-1.5">
                    {contact.role}
                  </span>
                  <h3 className="font-heading font-bold text-xl text-stone-900 uppercase tracking-tight">
                    {contact.name}
                  </h3>
                </div>

                <div className="mt-6 pt-4 border-t border-stone-100 text-xs sm:text-sm space-y-2.5">
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                      className="flex items-center gap-2.5 text-stone-800 hover:text-[#0072c0] font-mono font-bold transition-colors"
                    >
                      <i className="fa-solid fa-phone text-[#0072c0]"></i>
                      <span>{contact.phone}</span>
                    </a>
                  )}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2.5 text-stone-600 hover:text-[#0072c0] transition-colors"
                    >
                      <i className="fa-solid fa-envelope text-[#0072c0]"></i>
                      <span>{contact.email}</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox for arbejdsbilleder på Om os (gruppefoto udelukket) */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={workImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="Arbejdsbilleder hos KTS-Entreprise"
        />

      </div>
    </div>
  );
}
